import { Box, Icon, Tooltip } from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import type { GridTask, GridUser } from "../types";
import { useTaskStatusMutation } from "../hooks";
import { deriveCellState } from "../helpers/cellState";
import { chromeForState, tooltipFor } from "../helpers/cellChrome";
import TaskCellPopover from "./TaskCellPopover";

interface Props {
  task: GridTask | undefined;
  assignee: GridUser | undefined;
  today?: Date;
  fallbackRole?: string | null;
  /** Compact mode shrinks the cell footprint so more hires fit on screen. */
  density?: "comfortable" | "compact";
}

const BOX_SIZE = "16px";

export default function TaskCell({
  task, assignee, today = new Date(), fallbackRole = null, density = "comfortable",
}: Props) {
  const statusMutation = useTaskStatusMutation();
  const state = deriveCellState(task, today);
  const chrome = chromeForState(state);
  const tooltipLabel = tooltipFor(state, today);

  const handleBoxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!task || !chrome.primaryClickable) return;
    statusMutation.mutate({ taskId: task.id, status: "completed" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      (e.currentTarget as HTMLElement).click();
    }
  };

  // The body of the cell mounts the popover unless this cell is NA (no task).
  const body = (
    <Box
      role={state.kind === "na" ? undefined : "button"}
      tabIndex={state.kind === "na" ? undefined : 0}
      w="100%"
      h="100%"
      minH={density === "compact" ? "28px" : "44px"}
      px={density === "compact" ? 2 : 3}
      py={density === "compact" ? 0.5 : 1.5}
      bg={chrome.bgFill ?? "transparent"}
      borderBottom="1px solid"
      borderColor="gray.100"
      textAlign="center"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      cursor={state.kind === "na" ? "default" : "pointer"}
      transition="background-color 120ms ease"
      onKeyDown={handleKeyDown}
      _focusVisible={{ outline: "2px solid", outlineColor: "blue.400", outlineOffset: "-2px" }}
      aria-label={task ? `Task ${task.name}` : "No task"}
    >
      {chrome.showBox ? (
        <Tooltip label={tooltipLabel} placement="top" hasArrow openDelay={300}>
          <Box
            as="button"
            type="button"
            onClick={handleBoxClick}
            disabled={statusMutation.isPending}
            w={BOX_SIZE}
            h={BOX_SIZE}
            border="2px solid"
            borderColor={chrome.borderColor}
            borderRadius="sm"
            bg="transparent"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: `${chrome.borderColor}1A` }}
            aria-label="Complete task"
          >
            {statusMutation.isPending && <Icon as={FiCheck} boxSize={3} color={chrome.borderColor} />}
          </Box>
        </Tooltip>
      ) : (
        <Tooltip label={tooltipLabel} placement="top" hasArrow openDelay={300} isDisabled={!tooltipLabel}>
          <Box>{chrome.glyph}</Box>
        </Tooltip>
      )}
    </Box>
  );

  if (!task) return body;

  return (
    <TaskCellPopover task={task} assignee={assignee} fallbackRole={fallbackRole}>
      {body}
    </TaskCellPopover>
  );
}
